import format from 'date-fns/format';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  DetailsSidebar,
  Grid,
  Heading,
  Icon,
  Separator,
  Stack,
} from '@eluve/components';
import {
  useAppointmentSessionType,
  useLatestSuperbill,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  GoToPatientButton,
  useUserName,
} from '@eluve/smart-blocks';

import { AppointmentDetailsFragment } from './AppointmentDetails';
import { PatientDetails } from './PatientDetails';
import { SuperbillCard } from './SuperbillCard';

interface ReadonlyAppointmentDetailsProps {
  appointmentId: string;
}

const TimeFormat = 'h:mm a';

const LocationNameFragment = graphql(`
  fragment Location on Locations {
    __typename
    id
    name
  }
`);

export const ReadonlyAppointmentDetails: React.FC<
  ReadonlyAppointmentDetailsProps
> = ({ appointmentId }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { patientId, startDate, endDate, locationId, userId } =
    useCompleteFragment({
      fragment: AppointmentDetailsFragment,
      key: {
        id: appointmentId,
      },
    });

  const locationName = locationId
    ? cacheUtils.readFragment({
        fragment: LocationNameFragment,
        key: {
          id: locationId,
        },
        strict: false,
      })?.name
    : 'No location specified';

  const username = useUserName(userId);
  const appointmentSessionType = useAppointmentSessionType();

  const { downloadUrl, superbill } = useLatestSuperbill();
  const showArtifacts = superbill != null;

  return (
    <DetailsSidebar title="Details">
      <Stack className="p-4" gap={2}>
        {patientId && (
          <>
            <Stack
              className="border border-b-0 border-transparent px-4 pt-2"
              direction="horizontal"
              align="center"
              gap={3}
            >
              <div className="min-w-0 flex-1">
                <PatientDetails patientId={patientId} />
              </div>
              <FeatureFlaggedComponent flag="PATIENTS_VIEW">
                <GoToPatientButton tenantId={tenantId} patientId={patientId} />
              </FeatureFlaggedComponent>
            </Stack>
            <Separator className="my-2" />
          </>
        )}
        <Stack gap={4} className="px-4 lg:gap-6 lg:px-0">
          <div>
            <Heading size="xl" weight="semibold">
              Appointment
            </Heading>
          </div>
          <Grid
            align="center"
            gap={6}
            className="grid-cols-[auto] text-sm lg:grid-cols-[max-content_1fr] lg:gap-y-8"
          >
            <span className="text-text-tertiary">Date</span>
            <Stack
              className="border border-transparent"
              gap={2}
              direction="horizontal"
            >
              <Icon name="Calendar" />
              {startDate ? (
                format(new Date(startDate), 'PP')
              ) : (
                <span>No date specified</span>
              )}
            </Stack>
            <span className="text-text-tertiary">Time</span>

            <Stack
              className="border border-transparent"
              gap={2}
              direction="horizontal"
            >
              <Icon name="Clock" />
              <Stack gap={1} wrap direction="horizontal">
                <span>
                  {startDate
                    ? format(new Date(startDate), TimeFormat)
                    : 'Start Time'}
                </span>
                <Icon
                  name="MoveHorizontal"
                  size="xl"
                  className="w-3 text-text-tertiary"
                />
                <span>
                  {endDate ? format(new Date(endDate), TimeFormat) : 'End'}
                </span>
              </Stack>
            </Stack>

            <span className="text-text-tertiary">Location</span>
            <Stack
              className="truncate border border-transparent text-sm"
              gap={2}
              direction="horizontal"
            >
              <Icon name="MapPin" />
              {locationName ?? 'No location Specified'}
            </Stack>

            <FeatureFlaggedComponent flag="SESSION_TYPES">
              <span className="text-text-tertiary">Type</span>
              <Stack
                className="border border-transparent"
                gap={2}
                direction="horizontal"
              >
                <Icon name="Tag" />
                {appointmentSessionType?.name ?? 'No Type'}
              </Stack>
            </FeatureFlaggedComponent>

            <span className="text-text-tertiary">Owner</span>
            <Stack
              className="border border-transparent"
              gap={2}
              direction="horizontal"
            >
              <Icon name="User" />
              <span className="text-text-secondary">{username}</span>
            </Stack>
          </Grid>
        </Stack>

        {showArtifacts && <Separator className="mt-4 mb-2" />}

        {showArtifacts && (
          <Stack gap={4} className="px-2 lg:px-0">
            <div className="px-2 lg:px-0">
              <Heading size="xl" weight="semibold">
                Artifacts
              </Heading>
            </div>
            <SuperbillCard
              createdAt={superbill.createdAt}
              downloadUrl={downloadUrl}
            />
          </Stack>
        )}
      </Stack>
    </DetailsSidebar>
  );
};
